import { useController, type Control, type FieldPath, type FieldValues } from 'react-hook-form';
import { cx } from './utils';
import { FieldError } from './FieldError';

interface Option {
  value: string;
  label: string;
}

interface Props<T extends FieldValues> {
  name: FieldPath<T>;
  control: Control<T>;
  label?: string;
  options: readonly Option[];
  layout?: 'stacked' | 'inline';
}

export const RadioGroup = <T extends FieldValues>({
  name,
  control,
  label,
  options,
  layout = 'stacked',
}: Props<T>) => {
  const {
    field,
    fieldState: { error },
  } = useController({ name, control });
  const selected = field.value as string | undefined;

  return (
    <div>
      {label && <label className="lbl">{label}</label>}
      <div className={layout === 'inline' ? 'flex flex-wrap gap-4 mt-2' : ''}>
        {options.map((o) =>
          layout === 'inline' ? (
            <label
              key={o.value}
              className="flex cursor-pointer items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-ink-500"
            >
              <input
                type="radio"
                checked={selected === o.value}
                onChange={() => field.onChange(o.value)}
                onBlur={field.onBlur}
                className="h-[15px] w-[15px] accent-navy"
              />
              {o.label}
            </label>
          ) : (
            <label
              key={o.value}
              className={cx('radio-opt', selected === o.value && 'selected')}
            >
              <input
                type="radio"
                checked={selected === o.value}
                onChange={() => field.onChange(o.value)}
                onBlur={field.onBlur}
                className="mt-0.5 h-[15px] w-[15px] accent-teal"
              />
              <span className="text-[11px] font-bold uppercase tracking-wide text-ink-700">
                {o.label}
              </span>
            </label>
          ),
        )}
      </div>
      <FieldError message={error?.message} />
    </div>
  );
};
